import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from '../ui/FadeIn';
import { CheckIcon } from 'lucide-react';
import { ExitUpsellPopup } from '../ExitUpsellPopup';
const BASE_PRICE = 490;
const BUMP_PRICE = 290;
const bumps = [{
  id: 'protocolo',
  emoji: '🚀',
  headline: '¡SÍ! Activar mi "Protocolo de 7 Días" para resultados rápidos.',
  price: BUMP_PRICE,
  image: 'https://cdn.magicpatterns.com/uploads/iSQWY87umZirKrkag3Gn1A/Protocolo_de_7_dias.png',
  subtitle: 'Protocolo de 7 Días — El Reinicio Maestro',
  body: 'Sofia te lleva de la mano con un calendario exacto: qué tomar al despertar, almorzar y cenar para limpiar tu cuerpo y recuperar tu energía desde la primera semana.',
  bullets: ['Calendario diario "Paso a Paso".', 'Lista de compras express para el mercado.', 'Guía de los 3 alimentos que bloquean tu sanación.'],
  cta: 'Sí, quiero agregar el Protocolo a mi pedido'
}, {
  id: 'seguridad',
  emoji: '🛡️',
  headline: '¡AÑADIR SEGURIDAD! Guía de Interacciones y Contraindicaciones.',
  price: BUMP_PRICE,
  image: 'https://cdn.magicpatterns.com/uploads/pz6PPL92XTCjouZ8thSjin/Guia_de_Seguridad_Blindaje_Ancestral.png',
  subtitle: 'Guía de Seguridad — Blindaje Ancestral',
  body: 'Descubre qué plantas NUNCA debes mezclar con tus medicamentos actuales (presión, azúcar o colesterol) y conoce las dosis críticas para tu seguridad.',
  bullets: ['Semáforo de interacciones (Seguro vs. Peligroso).', 'Diccionario A-Z de contraindicaciones por receta.', 'Guía de dosis máximas recomendadas.'],
  cta: 'Sí, quiero agregar la Guía a mi pedido'
}];
export function PriceSection() {
  const [timeLeft, setTimeLeft] = useState(10 * 60);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [flashId, setFlashId] = useState<string | null>(null);
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  const minutes = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const seconds = (timeLeft % 60).toString().padStart(2, '0');
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const totalPrice = BASE_PRICE + selectedCount * BUMP_PRICE;
  const toggleBump = useCallback((id: string) => {
    setSelected(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
    setFlashId(id);
    setTimeout(() => setFlashId(null), 700);
  }, []);
  const [showUpsell, setShowUpsell] = useState(false);
  const getCheckoutUrl = () => {
    const hasProtocolo = !!selected['protocolo'];
    const hasSeguridad = !!selected['seguridad'];
    if (hasProtocolo && hasSeguridad) {
      return 'https://www.alivionaturalshop.com/cart/51525376311617:1';
    }
    if (hasProtocolo) {
      return 'https://www.alivionaturalshop.com/cart/51525375525185:1';
    }
    if (hasSeguridad) {
      return 'https://www.alivionaturalshop.com/cart/51525375689025:1';
    }
    return 'https://www.alivionaturalshop.com/cart/51513616499009:1';
  };
  const handleCheckout = () => {
    const hasBumps = !!selected['protocolo'] || !!selected['seguridad'];
    if (hasBumps) {
      window.open(getCheckoutUrl(), '_blank', 'noopener,noreferrer');
    } else {
      setShowUpsell(true);
    }
  };
  return <section className="bg-ancestral-green py-14 md:py-20 text-ancestral-cream relative" id="oferta" data-id="element-286">
      <div className="container mx-auto px-4 relative z-10" data-id="element-287">
        <div className="max-w-lg mx-auto" data-id="element-288">
          {/* Section header */}
          <FadeIn data-id="element-289">
            <div className="text-center mb-6" data-id="element-290">
              <p className="text-xs uppercase tracking-[0.2em] text-ancestral-gold/70 font-semibold mb-2" data-id="element-291">
                Oferta exclusiva por tiempo limitado
              </p>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-ancestral-gold" data-id="element-292">
                Tu Recetario Ancestral + 10 Bonos
              </h2>
            </div>
          </FadeIn>

          {/* Unified card */}
          <FadeIn delay={0.15} data-id="element-293">
            <div className="bg-black/20 border border-ancestral-gold/25 rounded-2xl overflow-hidden shadow-2xl" data-id="element-294">
              {/* Product image */}
              <div className="p-6 pb-0" data-id="element-295">
                <div className="max-w-[320px] mx-auto" data-id="element-296">
                  <img src="https://cdn.magicpatterns.com/uploads/8qwq7iJH71X98zmPKkuWDH/WhatsApp_Image_2026-03-16_at_20.07.50.jpg" alt="El Gran Recetario Ancestral" className="w-full rounded-xl shadow-lg border border-ancestral-gold/15" data-id="element-297" />
                </div>
              </div>

              <div className="p-6 pt-5 space-y-5" data-id="element-298">
                {/* Countdown */}
                <div className="text-center" data-id="element-299">
                  <p className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-2" data-id="element-300">
                    ⏰ Esta oferta expira en
                  </p>
                  <div className="flex items-center justify-center gap-2.5" data-id="element-301">
                    <div className="bg-ancestral-cream/10 border border-ancestral-gold/20 rounded-lg px-4 py-2 text-center min-w-[64px]" data-id="element-302">
                      <span className="text-3xl font-bold text-ancestral-cream tabular-nums" data-id="element-303">
                        {minutes}
                      </span>
                      <p className="text-[9px] uppercase tracking-widest text-ancestral-gold/50" data-id="element-304">
                        Min
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-ancestral-gold/30" data-id="element-305">
                      :
                    </span>
                    <div className="bg-ancestral-cream/10 border border-ancestral-gold/20 rounded-lg px-4 py-2 text-center min-w-[64px]" data-id="element-306">
                      <span className="text-3xl font-bold text-ancestral-cream tabular-nums" data-id="element-307">
                        {seconds}
                      </span>
                      <p className="text-[9px] uppercase tracking-widest text-ancestral-gold/50" data-id="element-308">
                        Seg
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-ancestral-gold/10" data-id="element-309" />

                {/* Dynamic Price */}
                <div className="text-center" data-id="element-310">
                  <div className="inline-block bg-ancestral-gold text-ancestral-green font-bold px-4 py-1 rounded-full text-xs tracking-widest uppercase mb-3" data-id="element-311">
                    50% DE DESCUENTO
                  </div>
                  <div className="mb-1" data-id="element-312">
                    <span className="text-sm text-ancestral-cream/50 uppercase tracking-wider" data-id="element-313">
                      Precio normal:{' '}
                    </span>
                    <span className="text-lg font-bold text-red-400 line-through ml-1" data-id="element-314">
                      $980 UYU
                    </span>
                  </div>
                  <p className="text-ancestral-gold font-bold uppercase tracking-widest text-xs mb-1" data-id="element-315">
                    HOY SOLO:
                  </p>
                  <div className="flex items-baseline justify-center gap-1.5" data-id="element-316">
                    <motion.span key={totalPrice} initial={{
                    scale: 1.2,
                    color: '#4ade80'
                  }} animate={{
                    scale: 1,
                    color: '#C9A84C'
                  }} transition={{
                    duration: 0.5,
                    type: 'spring',
                    stiffness: 200
                  }} className="text-5xl md:text-6xl font-serif font-bold drop-shadow-lg" data-id="element-317">
                      ${totalPrice.toLocaleString()}
                    </motion.span>
                    <span className="text-xl font-bold text-ancestral-gold" data-id="element-318">
                      UYU
                    </span>
                  </div>
                  <AnimatePresence data-id="element-319">
                    {selectedCount > 0 && <motion.p initial={{
                    opacity: 0,
                    y: -5
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -5
                  }} className="text-[10px] text-emerald-400 mt-1 font-semibold" data-id="element-320">
                        Recetario $490 + {selectedCount} extra
                        {selectedCount > 1 ? 's' : ''} × $290
                      </motion.p>}
                  </AnimatePresence>
                  <p className="text-[10px] text-ancestral-cream/35 mt-1" data-id="element-321">
                    Pesos uruguayos
                  </p>
                </div>

                <div className="border-t border-ancestral-gold/10" data-id="element-322" />

                {/* Social proof */}
                <div className="flex items-center justify-center gap-5" data-id="element-323">
                  <div className="flex items-center gap-1.5" data-id="element-324">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-ancestral-gold" data-id="element-325">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" data-id="element-326" />
                      <polyline points="7 10 12 15 17 10" data-id="element-327" />
                      <line x1="12" y1="15" x2="12" y2="3" data-id="element-328" />
                    </svg>
                    <span className="text-xs font-semibold text-ancestral-cream/70" data-id="element-329">
                      +3,454 descargas
                    </span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-330">
                    <div className="flex gap-0.5" data-id="element-331">
                      {[1, 2, 3, 4].map(i => <svg key={i} viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-332">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-333" />
                        </svg>)}
                      <svg viewBox="0 0 20 20" className="w-3.5 h-3.5" data-id="element-334">
                        <defs data-id="element-335">
                          <linearGradient id="hs" data-id="element-336">
                            <stop offset="50%" stopColor="#C9A84C" data-id="element-337" />
                            <stop offset="50%" stopColor="#C9A84C" stopOpacity="0.3" data-id="element-338" />
                          </linearGradient>
                        </defs>
                        <path fill="url(#hs)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-339" />
                      </svg>
                    </div>
                    <span className="text-xs font-semibold text-ancestral-cream/70" data-id="element-340">
                      4.5/5
                    </span>
                  </div>
                </div>

                <div className="border-t border-ancestral-gold/10" data-id="element-341" />

                {/* CTA Button */}
                <button onClick={handleCheckout} className="flex items-center justify-center w-full bg-emerald-500 hover:bg-emerald-400 text-white border-2 border-emerald-400 rounded-xl py-4 px-6 shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-342">
                  <span className="font-serif font-bold text-base md:text-lg text-center uppercase tracking-wide" data-id="element-343">
                    Quiero Mi Recetario Ahora →
                  </span>
                </button>

                {/* Trust badges */}
                <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-344">
                  <div className="flex items-center gap-1.5 text-ancestral-cream/60" data-id="element-345">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-346">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" data-id="element-347" />
                    </svg>
                    <span className="text-[11px] font-medium" data-id="element-348">
                      Acceso inmediato
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-ancestral-cream/60" data-id="element-349">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-350">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" data-id="element-351" />
                      <path d="M7 11V7a5 5 0 0110 0v4" data-id="element-352" />
                    </svg>
                    <span className="text-[11px] font-medium" data-id="element-353">
                      Pago 100% seguro
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-ancestral-cream/60" data-id="element-354">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-ancestral-gold" data-id="element-355">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" data-id="element-356" />
                      <line x1="12" y1="18" x2="12.01" y2="18" data-id="element-357" />
                    </svg>
                    <span className="text-[11px] font-medium" data-id="element-358">
                      Cualquier dispositivo
                    </span>
                  </div>
                </div>

                {/* ═══ ORDER BUMPS ═══ */}
                <div className="border-t border-ancestral-gold/10" data-id="element-359" />

                <p className="text-center text-[10px] text-ancestral-gold font-semibold uppercase tracking-wider" data-id="element-360">
                  🔥 87% de los clientes añaden ambos
                </p>

                <div className="space-y-3" data-id="element-361">
                  {bumps.map(bump => {
                  const isSelected = !!selected[bump.id];
                  const isFlashing = flashId === bump.id;
                  return <motion.div key={bump.id} onClick={() => toggleBump(bump.id)} whileHover={{
                    boxShadow: '0 0 24px rgba(201,168,76,0.35)',
                    borderColor: '#C9A84C'
                  }} whileTap={{
                    scale: 0.98
                  }} className={`relative cursor-pointer rounded-xl border-2 border-dashed overflow-hidden transition-all duration-300 ${isSelected ? 'border-ancestral-gold shadow-lg' : 'border-gray-400/50'}`} data-id="element-362">
                        {/* Flash overlay */}
                        <AnimatePresence data-id="element-363">
                          {isFlashing && <motion.div initial={{
                        opacity: 0.8,
                        scale: 0.95
                      }} animate={{
                        opacity: 0,
                        scale: 1.05
                      }} exit={{
                        opacity: 0
                      }} transition={{
                        duration: 0.7
                      }} className="absolute inset-0 bg-gradient-to-r from-ancestral-gold/40 via-yellow-300/30 to-ancestral-gold/40 pointer-events-none z-20" data-id="element-364" />}
                        </AnimatePresence>

                        {/* Top banner */}
                        <div className="bg-ancestral-gold text-ancestral-green text-center py-1 px-3" data-id="element-365">
                          <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-366">
                            ⭐ Altamente recomendado ⭐
                          </span>
                        </div>

                        {/* Content */}
                        <div className="bg-white p-3" data-id="element-367">
                          {/* Checkbox + headline */}
                          <div className="flex items-start gap-2.5 mb-2.5" data-id="element-368">
                            <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-all duration-200 ${isSelected ? 'bg-ancestral-green border-ancestral-green' : 'border-gray-300 bg-white'}`} data-id="element-369">
                              {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-370" />}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-371">
                              <p className="font-bold text-xs text-gray-900 leading-snug" data-id="element-372">
                                {bump.emoji} {bump.headline}{' '}
                                <span className="text-emerald-600" data-id="element-373">
                                  ${bump.price} UYU
                                </span>
                              </p>
                            </div>
                          </div>

                          {/* Image + description row */}
                          <div className="flex flex-col sm:flex-row gap-3" data-id="element-374">
                            <div className="w-full sm:w-32 flex-shrink-0" data-id="element-375">
                              <img src={bump.image} alt={bump.subtitle} className="w-full max-w-[200px] mx-auto sm:max-w-none rounded-lg shadow-sm" data-id="element-376" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-377">
                              <p className="font-bold text-[11px] text-gray-800 mb-1" data-id="element-378">
                                {bump.subtitle}
                              </p>
                              <p className="text-[10px] text-gray-600 leading-relaxed mb-1.5" data-id="element-379">
                                {bump.body}
                              </p>
                              <div className="flex items-center gap-1.5 mb-1.5" data-id="element-380">
                                <span className="text-[10px] text-gray-400 line-through" data-id="element-381">
                                  ${bump.price * 3} UYU
                                </span>
                                <span className="text-xs font-bold text-ancestral-green" data-id="element-382">
                                  ${bump.price} UYU
                                </span>
                                <span className="text-[9px] bg-red-500 text-white px-1 py-0.5 rounded font-bold" data-id="element-383">
                                  -67%
                                </span>
                              </div>
                              <p className="text-[9px] text-gray-400 italic" data-id="element-384">
                                ¡Menos de lo que cuesta un café!
                              </p>
                            </div>
                          </div>

                          {/* Bullets */}
                          <ul className="mt-2 space-y-0.5" data-id="element-385">
                            {bump.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-1.5 text-[10px] text-gray-700" data-id="element-386">
                                <span className="text-emerald-500 flex-shrink-0" data-id="element-387">
                                  ✅
                                </span>
                                <span data-id="element-388">{bullet}</span>
                              </li>)}
                          </ul>

                          {/* CTA */}
                          <div className={`mt-2.5 text-center py-2 rounded-lg text-xs font-bold transition-all duration-200 ${isSelected ? 'bg-ancestral-green text-white' : 'bg-emerald-500 text-white'}`} data-id="element-389">
                            {isSelected ? '✓ Añadido a tu pedido' : bump.cta}
                          </div>

                          <p className="text-center text-[9px] text-gray-400 mt-1.5" data-id="element-390">
                            🔒 Oferta disponible solo en esta página
                          </p>
                        </div>
                      </motion.div>;
                })}
                </div>

                {/* ═══ MINI CHECKOUT SUMMARY ═══ */}
                <div className="border-t border-ancestral-gold/10" data-id="element-391" />

                <div className="bg-ancestral-cream/5 border border-ancestral-gold/15 rounded-xl p-4 space-y-2.5" data-id="element-392">
                  <p className="text-[10px] uppercase tracking-widest text-ancestral-gold/60 font-bold text-center mb-1" data-id="element-393">
                    🛒 Resumen de tu pedido
                  </p>

                  {/* Line items */}
                  <div className="space-y-1.5" data-id="element-394">
                    <div className="flex items-center justify-between text-xs" data-id="element-395">
                      <span className="text-ancestral-cream/80" data-id="element-396">
                        📖 Recetario Ancestral + 10 Bonos
                      </span>
                      <span className="text-ancestral-cream font-semibold" data-id="element-397">
                        $490
                      </span>
                    </div>
                    {bumps.map(bump => <div key={bump.id} className="flex items-center justify-between text-xs" data-id="element-398">
                        <span className={selected[bump.id] ? 'text-ancestral-cream/80' : 'text-ancestral-cream/30'} data-id="element-399">
                          {bump.emoji} {bump.subtitle}
                        </span>
                        {selected[bump.id] ? <span className="text-emerald-400 font-semibold" data-id="element-400">
                            +${bump.price}
                          </span> : <span className="text-ancestral-cream/25 text-[10px] italic" data-id="element-401">
                            No añadido
                          </span>}
                      </div>)}
                  </div>

                  {/* Divider */}
                  <div className="border-t border-ancestral-gold/10" data-id="element-402" />

                  {/* Total */}
                  <div className="flex items-center justify-between" data-id="element-403">
                    <span className="text-sm font-bold text-ancestral-cream uppercase tracking-wide" data-id="element-404">
                      Total:
                    </span>
                    <motion.span key={totalPrice} initial={{
                    scale: 1.15,
                    color: '#4ade80'
                  }} animate={{
                    scale: 1,
                    color: '#C9A84C'
                  }} transition={{
                    duration: 0.4,
                    type: 'spring',
                    stiffness: 200
                  }} className="text-2xl font-serif font-bold" data-id="element-405">
                      ${totalPrice.toLocaleString()} UYU
                    </motion.span>
                  </div>

                  {/* Checkout CTA */}
                  <button onClick={handleCheckout} className="flex items-center justify-center w-full bg-emerald-500 hover:bg-emerald-400 text-white border-2 border-emerald-400 rounded-xl py-3.5 px-5 shadow-lg hover:shadow-xl transition-all duration-300 mt-1" data-id="element-406">
                    <span className="font-serif font-bold text-sm md:text-base text-center uppercase tracking-wide" data-id="element-407">
                      Finalizar Compra →
                    </span>
                  </button>

                  <p className="text-center text-[9px] text-ancestral-cream/35" data-id="element-408">
                    🔒 Pago 100% seguro · Acceso inmediato
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
      {/* Exit-intent upsell popup */}
      <ExitUpsellPopup isOpen={showUpsell} onClose={() => setShowUpsell(false)} data-id="element-409" />
    </section>;
}